import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './BranchTracking.module.css';
import { formatOrderId } from '../../utils/formatOrderId';

const STAGES = ['Order Placed', 'Confirmed', 'In Transit', 'Delivered'];

const STAGE_INDEX = {
  'Pending':    0,
  'Confirmed':  1,
  'In Transit': 2,
  'On its way': 2, // backward compat for old Firestore records
  'Delivered':  3,
};

function estimateETA(status) {
  if (status === 'In Transit' || status === 'On its way') return 'Estimated arrival: 2–4 hours';
  if (status === 'Pending')    return 'Awaiting supplier confirmation';
  if (status === 'Confirmed')  return 'Preparing for dispatch';
  if (status === 'Delivered')  return 'Delivered';
  return '—';
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function formatTotal(total) {
  if (typeof total === 'number') return `₱${total.toLocaleString()}`;
  return total ?? '—';
}

function StatusBadge({ status }) {
  const cls = styles[`badge_${status.replace(/\s+/g, '_')}`];
  return <span className={`${styles.badge} ${cls ?? ''}`}>{status}</span>;
}

function ProgressStepper({ status }) {
  const current = STAGE_INDEX[status] ?? 0;
  return (
    <div className={styles.stepper}>
      {STAGES.map((stage, i) => {
        const done   = i < current;
        const active = i === current;
        const future = i > current;
        return (
          <div key={stage} className={styles.stepItem}>
            {i > 0 && <div className={`${styles.line} ${done || active ? styles.lineDone : ''}`} />}
            <div className={styles.circleWrap}>
              <div className={`${styles.circle} ${done ? styles.circleDone : ''} ${active ? styles.circleActive : ''} ${future ? styles.circleFuture : ''}`}>
                {done && (
                  <svg viewBox="0 0 10 10" className={styles.checkIcon}>
                    <polyline points="1.5,5 4,7.5 8.5,2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {active && <span className={styles.pulseDot} />}
              </div>
            </div>
            <span className={`${styles.stepLabel} ${done ? styles.labelDone : ''} ${active ? styles.labelActive : ''}`}>{stage}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function BranchTracking() {
  const { user }                              = useAuth();
  const [trackingData, setTrackingData]       = useState([]);
  const [expanded, setExpanded]               = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'orders'),
      where('branchId', '==', user.uid)
    );
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0));
      setTrackingData(docs);
    });
    return unsub;
  }, [user?.uid]);

  function toggleRow(id) {
    setExpanded(prev => (prev === id ? null : id));
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Order Tracking" description="Track your supply deliveries" />

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order ID</th><th>Items</th><th>Total</th><th>Last Updated</th><th>Status</th><th>ETA</th><th></th>
            </tr>
          </thead>
          <tbody>
            {trackingData.length === 0 ? (
              <tr><td colSpan={7} className={styles.empty}>No orders found.</td></tr>
            ) : (
              trackingData.map((item, idx) => (
                <>
                  <tr
                    key={item.id}
                    className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}
                    onClick={() => toggleRow(item.id)}
                  >
                    <td className={styles.orderId}>{formatOrderId(item.id)}</td>
                    <td className={styles.items}>{item.details || (item.items || []).map(i => `${i.productId} (${i.qty}x)`).join(', ') || '—'}</td>
                    <td className={styles.total}>{formatTotal(item.total)}</td>
                    <td>{formatDate(item.updatedAt)}</td>
                    <td><StatusBadge status={item.status} /></td>
                    <td className={styles.etaCol}>{item.estimatedArrival || estimateETA(item.status)}</td>
                    <td>
                      <button className={styles.expandBtn}>{expanded === item.id ? '▲' : '▼'}</button>
                    </td>
                  </tr>
                  {expanded === item.id && (
                    <tr key={`${item.id}-tracker`} className={styles.trackerRow}>
                      <td colSpan={7}><ProgressStepper status={item.status} /></td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
